import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  exhaustMap,
  filter,
  take,
  timer,
} from 'rxjs';
import { environment } from 'src/environments/environment';

export type RouteLoading = {
  route: string;
  loading: boolean;
};
export type Branch = {
  name: string;
};
type AppConstant = {
  coinFeatures: Record<string, number>; // Equivalent to Map<String, Number>
  coinPackages: Record<string, Record<string, number>>; // Equivalent to Map<String, Map<String, Number>>
  faculties: Record<string, Branch[]>; // Equivalent to Map<String, Set<String>>
  serverTime: number; // Equivalent to Long (in Java, this is often represented as a number in TypeScript)
};

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private readonly loadingSubject$ = new BehaviorSubject<
    RouteLoading | undefined
  >(undefined);
  public readonly isLoading$ = this.loadingSubject$.asObservable();
  public appConstant$ = signal<AppConstant | undefined>(undefined);
  private readonly loadingCounts = new Map<string, number>();
  private constantsFailureLogged = false;
  private readonly http;
  constructor(backend: HttpBackend) {
    this.http = new HttpClient(backend);
    this.loadAppConstantsHttp();
  }

  public setLoading(routeLoading: RouteLoading) {
    const currentCount = this.loadingCounts.get(routeLoading.route) ?? 0;
    const nextCount = routeLoading.loading
      ? currentCount + 1
      : Math.max(0, currentCount - 1);

    if (nextCount === 0) {
      this.loadingCounts.delete(routeLoading.route);
    } else {
      this.loadingCounts.set(routeLoading.route, nextCount);
    }

    this.loadingSubject$.next({
      route: routeLoading.route,
      loading: nextCount > 0,
    });
  }

  private loadAppConstantsHttp() {
    return timer(0, 5_000)
      .pipe(
        exhaustMap(() =>
          this.http.get<any>(`${environment.apiUrl}app/constants`).pipe(
            catchError((error) => {
              if (!this.constantsFailureLogged) {
                console.warn(
                  '[AppService] App constants unavailable; retrying in the background.',
                  error
                );
                this.constantsFailureLogged = true;
              }
              return EMPTY;
            })
          )
        ),
        filter((res) => res?.body != null),
        take(1)
      )
      .subscribe((res) => {
        this.constantsFailureLogged = false;
        this.appConstant$.set(res.body);
      });
  }
}
